import { useState } from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import { useIsFeatureEnabled } from '@/stores/settingsStore';
import { updateGconSessionTargets } from '@/api/gcon';
import {
  XMarkIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import HotSpotRow from './HotSpotRow';
import SummaryCard from './SummaryCard';
import BarList from './BarList';
import InsightsPanel from './InsightsPanel';
import LevelizationView from './LevelizationView';
import ByUnitView from './ByUnitView';
import BopView from './BopView';
import ProductionView from './ProductionView';
import {
  moduleBadge,
  formatCo2e,
  buildMonthList,
  sliceResultByMonths,
  downloadSourceInventory,
  buildAiContext,
} from '@/utils/gconUtils';

/** The analysis views for one saved GCON session. */
const GconResults = ({ session }) => {
  const { result } = session;
  const [sortBy, setSortBy] = useState('emissions');
  const [showInsights, setShowInsights] = useState(false);
  const [bopTargets, setBopTargets] = useState(session.bop_targets ?? {});
  const saveBopTargets = async targets => {
    const { data } = await updateGconSessionTargets(session.id, targets);
    setBopTargets(data.bop_targets ?? {});
  };
  // GCON's AI is enabled per deployment separately from GCON itself
  const aiEnabled = useIsFeatureEnabled('gcon_ai');
  const [activeTab, setActiveTab] = useState('byunit');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState([]);
  const [rangeStart, setRangeStart] = useState(0);
  const [rangeEnd, setRangeEnd] = useState(-1);
  const [excluded, setExcluded] = useState([]); // month keys dropped from the range

  // --- Month range + per-month exclusions ----------------------------------
  const months = result ? buildMonthList(result) : [];
  const endIdx = rangeEnd < 0 ? months.length - 1 : Math.min(rangeEnd, months.length - 1);
  const startIdx = Math.min(rangeStart, endIdx);
  const rangeMonths = months.slice(startIdx, endIdx + 1);
  const activeMonths = rangeMonths.filter(m => !excluded.includes(m.key));
  const isFullSelection = startIdx === 0 && endIdx === months.length - 1 && activeMonths.length === months.length;
  const rangeKeys = isFullSelection ? null : activeMonths.map(m => m.key);
  const view = result ? sliceResultByMonths(result, rangeKeys) : null;
  const omittedCount = rangeMonths.length - activeMonths.length;

  const rangeLabel = (() => {
    if (!months.length || isFullSelection) return null;
    const span = startIdx === endIdx ? months[startIdx].label : `${months[startIdx].label} – ${months[endIdx].label}`;
    return omittedCount ? `${span} · ${omittedCount} excluded` : span;
  })();

  // Never let the selection empty out
  const toggleMonth = key =>
    setExcluded(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      return activeMonths.length <= 1 ? prev : [...prev, key];
    });

  const resetRange = () => {
    setRangeStart(0);
    setRangeEnd(-1);
    setExcluded([]);
  };

  // --- Equipment filtering + sorting ---------------------------------------
  const availableModules = [...new Set((view?.hot_spots ?? []).map(s => s.module).filter(Boolean))].sort();

  const toggleModule = m => setModuleFilter(prev => (prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]));

  const query = search.trim().toLowerCase();
  const sortedHotSpots = (view?.hot_spots ?? [])
    .filter(s => !moduleFilter.length || moduleFilter.includes(s.module))
    .filter(
      s =>
        !query ||
        `${s.source_name} ${s.module ?? ''} ${s.location ?? ''} ${s.sub_location ?? ''}`.toLowerCase().includes(query),
    )
    .sort((a, b) => {
      if (sortBy === 'emissions_asc') return a.total_co2e - b.total_co2e;
      if (sortBy === 'avg') return b.avg_monthly_co2e - a.avg_monthly_co2e;
      if (sortBy === 'name') return a.source_name.localeCompare(b.source_name);
      if (sortBy === 'module') return (a.module ?? '').localeCompare(b.module ?? '') || b.total_co2e - a.total_co2e;
      return b.total_co2e - a.total_co2e;
    });

  const globalMax = Math.max(...sortedHotSpots.map(s => s.total_co2e), 1);
  // Shared axis for the dot strips, so monthly distributions compare across rows
  const globalMonthlyMax = Math.max(...sortedHotSpots.flatMap(s => (s.monthly_data ?? []).map(d => d.co2e)), 1);
  const filtersActive = moduleFilter.length > 0 || query.length > 0;

  // Results view
  return (
    <div className="flex flex-1 min-w-0 h-full bg-gray-50">
      {/* Left sidebar — summary */}
      {sidebarOpen ? (
        <div className="w-72 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-y-auto">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-sm font-semibold text-gray-800 truncate" title={session.name}>
                {session.name}
              </h1>
              <div className="flex items-center gap-2">
                <Link to="/gcon" className="text-xs text-teal-500 hover:text-teal-400">
                  All analyses
                </Link>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  title="Collapse sidebar"
                  className="p-1 rounded hover:bg-gray-100 text-gray-400"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-400">
              {rangeLabel
                ? rangeLabel
                : result.reporting_years?.length
                  ? `Reporting year${result.reporting_years.length > 1 ? 's' : ''}: ${result.reporting_years.join(', ')}`
                  : 'All reporting periods'}
            </p>
          </div>

          {/* KPI cards */}
          <div className="p-4 flex flex-col gap-3">
            <SummaryCard
              label="Total CO₂e"
              value={formatCo2e(view.total_co2e)}
              sub={rangeLabel ? 'all sources in range' : 'across all sources'}
              icon={FireIcon}
              color="bg-orange-50 text-orange-600"
            />
            <SummaryCard
              label="Total Sources"
              value={result.total_sources}
              sub="unique emission sources"
              icon={ChartBarIcon}
              color="bg-teal-50 text-teal-600"
            />
            <SummaryCard
              label="Hot Spots Shown"
              value={view.hot_spots.length}
              sub={rangeLabel ? 'with data in range' : 'ranked sources'}
              icon={ArrowTrendingUpIcon}
              color="bg-red-50 text-red-500"
            />
          </div>

          {/* By emissions source (module) */}
          {view.top_modules?.length > 0 && (
            <div className="px-4 pb-4">
              <BarList items={view.top_modules} label="By Emissions Source" />
            </div>
          )}

          {/* By location */}
          {view.top_locations?.length > 0 && (
            <div className="px-4 pb-4">
              <BarList items={view.top_locations} label="By Location" />
            </div>
          )}

          {result.warnings?.length > 0 && (
            <div className="px-4 pb-4">
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 flex flex-col gap-1.5">
                {result.warnings.map(w => (
                  <p key={w} className="text-xs text-amber-800 flex gap-1.5">
                    <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                    {w}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Excluded sources — the total omits these, so say so */}
          {result.excluded_sources?.length > 0 && (
            <div className="px-4 pb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Excluded from analysis</p>
              <div className="bg-gray-50 rounded-lg p-2.5 flex flex-col gap-1">
                {result.excluded_sources.map(e => (
                  <div key={e.source_name} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500 truncate" title={e.source_name}>
                      {e.source_name}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0 line-through">{formatCo2e(e.total_co2e)}</span>
                  </div>
                ))}
                <p className="text-xs text-gray-400 mt-1 pt-1 border-t border-gray-200">
                  {formatCo2e(result.excluded_co2e ?? 0)} CO₂e omitted from all totals
                </p>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="mt-auto p-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-2 font-medium">Legend</p>
            <div className="flex items-center gap-2">
              <FireIcon className="w-3.5 h-3.5 text-orange-500" />
              <span className="text-xs text-gray-500">Top 3 emitters</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="w-11 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-3">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            title="Expand sidebar"
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
          <div className="border-t border-gray-100 w-6" />
          <FireIcon className="w-4 h-4 text-orange-500" title={`${formatCo2e(view.total_co2e)} CO₂e`} />
          <span className="text-[10px] font-medium text-gray-400 tracking-wide" style={{ writingMode: 'vertical-rl' }}>
            {formatCo2e(view.total_co2e)} CO₂e · {view.hot_spots.length} hot spots
          </span>
        </div>
      )}

      {/* Right panel */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Tab bar + actions */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="join">
            {[
              { key: 'byunit', label: 'By Unit' },
              { key: 'hotspots', label: 'Hot Spots' },
              {
                key: 'levelization',
                label: `Levelization${view.levelized_sources?.length ? ` (${view.levelized_sources.length})` : ''}`,
              },
              { key: 'bop', label: 'B.O.P.' },
              { key: 'production', label: 'Production' },
            ].map(t => (
              <button
                key={t.key}
                type="button"
                className={`join-item btn btn-sm ${activeTab === t.key ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {/* Month range */}
            {months.length > 1 && (
              <div className="flex items-center gap-1.5">
                <CalendarDaysIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <select
                  className="select select-xs select-bordered w-28"
                  value={startIdx}
                  onChange={e => {
                    const v = Number(e.target.value);
                    setRangeStart(v);
                    if (v > endIdx) setRangeEnd(v);
                  }}
                >
                  {months.map((m, i) => (
                    <option key={m.key} value={i}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-400">→</span>
                <select
                  className="select select-xs select-bordered w-28"
                  value={endIdx}
                  onChange={e => {
                    const v = Number(e.target.value);
                    setRangeEnd(v);
                    if (v < startIdx) setRangeStart(v);
                  }}
                >
                  {months.map((m, i) => (
                    <option key={m.key} value={i} disabled={i < startIdx}>
                      {m.label}
                    </option>
                  ))}
                </select>
                {/* Per-month exclusions */}
                <div className="dropdown dropdown-end">
                  <div
                    tabIndex={0}
                    role="button"
                    title="Exclude individual months"
                    className={`btn btn-xs gap-1 ${omittedCount ? 'btn-primary' : 'btn-ghost text-gray-400'}`}
                  >
                    <FunnelIcon className="w-3.5 h-3.5" />
                    {omittedCount ? `${activeMonths.length}/${rangeMonths.length}` : ''}
                  </div>
                  <div
                    tabIndex={0}
                    className="dropdown-content z-50 mt-1 w-72 bg-white rounded-lg border border-gray-200 shadow-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-600">Months included</p>
                      {omittedCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setExcluded([])}
                          className="text-xs text-teal-500 hover:text-teal-400"
                        >
                          Include all
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                      {rangeMonths.map(m => {
                        const off = excluded.includes(m.key);
                        const last = !off && activeMonths.length <= 1;
                        return (
                          <button
                            key={m.key}
                            type="button"
                            onClick={() => toggleMonth(m.key)}
                            disabled={last}
                            title={last ? 'At least one month must stay selected' : undefined}
                            className={`text-xs px-2 py-1 rounded border transition-colors ${
                              off
                                ? 'bg-gray-50 text-gray-300 border-gray-100 line-through'
                                : 'bg-teal-50 text-teal-700 border-teal-200 font-medium hover:border-teal-400'
                            } ${last ? 'cursor-not-allowed' : ''}`}
                          >
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      {omittedCount
                        ? `${omittedCount} month${omittedCount > 1 ? 's' : ''} excluded from all tabs`
                        : 'Click a month to exclude it from all tabs'}
                    </p>
                  </div>
                </div>

                {!isFullSelection && (
                  <button
                    type="button"
                    onClick={resetRange}
                    className="btn btn-xs btn-ghost text-gray-400"
                    title="Reset to full range"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
            {aiEnabled && (
              <button
                type="button"
                onClick={() => setShowInsights(v => !v)}
                className={`btn btn-sm gap-1.5 ${showInsights ? 'btn-ghost text-teal-600' : 'btn-primary'}`}
              >
                <SparklesIcon className="w-4 h-4" />
                AI Insights
              </button>
            )}
          </div>
        </div>

        {/* Tab content */}
        {activeTab === 'hotspots' && (
          <>
            {/* Filter + sort bar */}
            <div className="px-6 py-3.5 border-b border-gray-100 bg-white flex-shrink-0 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search equipment, location…"
                    // !pl-9 / !pr-8 — DaisyUI's .input-sm padding otherwise wins
                    // and the placeholder runs under the icons.
                    className="input input-sm input-bordered w-full !pl-9 !pr-8 text-xs"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-auto">
                  {result.source_inventory?.length > 0 && (
                    <button
                      type="button"
                      onClick={() => downloadSourceInventory(result)}
                      title={`Download all ${result.source_inventory.length} sources as CSV`}
                      className="btn btn-xs btn-ghost gap-1 text-gray-500"
                    >
                      <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                      Inventory
                    </button>
                  )}
                  <span className="text-xs text-gray-400 flex-shrink-0">Sort</span>
                  <select
                    className="select select-sm select-bordered w-44 text-xs"
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                  >
                    <option value="emissions">Emissions (high → low)</option>
                    <option value="emissions_asc">Emissions (low → high)</option>
                    <option value="avg">Avg per month</option>
                    <option value="name">Name (A–Z)</option>
                    <option value="module">Module</option>
                  </select>
                </div>
              </div>

              {/* Module chips */}
              {availableModules.length > 1 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {availableModules.map(m => {
                    const on = moduleFilter.includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => toggleModule(m)}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-colors ${
                          on
                            ? `${moduleBadge(m)} border-current`
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        {m}
                      </button>
                    );
                  })}
                  {filtersActive && (
                    <button
                      type="button"
                      onClick={() => {
                        setModuleFilter([]);
                        setSearch('');
                      }}
                      className="text-xs text-teal-500 hover:text-teal-400 ml-1 px-1 py-1"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {sortedHotSpots.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 gap-2 text-center">
                  <MagnifyingGlassIcon className="w-6 h-6 text-gray-300" />
                  <p className="text-sm text-gray-400">No equipment matches these filters.</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-400 mb-3">
                    Showing {sortedHotSpots.length} of {view.hot_spots.length} sources
                    {rangeLabel ? ` · ${rangeLabel}` : ''}
                  </p>
                  <div className="flex flex-col gap-3">
                    {sortedHotSpots.map((spot, i) => (
                      <HotSpotRow
                        key={spot.source_id}
                        spot={spot}
                        rank={i + 1}
                        globalMax={globalMax}
                        globalMonthlyMax={globalMonthlyMax}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}
        {activeTab === 'byunit' && (
          <div className="flex-1 min-h-0">
            <ByUnitView data={view.unit_monthly} reportingYears={result.reporting_years} />
          </div>
        )}
        {activeTab === 'bop' && (
          <div className="flex-1 min-h-0">
            <BopView
              unitData={view.unit_monthly}
              moduleData={view.module_monthly}
              targets={bopTargets}
              onSaveTargets={saveBopTargets}
            />
          </div>
        )}
        {activeTab === 'production' && (
          <div className="flex-1 min-h-0">
            <ProductionView data={view.production_breakdown} reportingYears={result.reporting_years} />
          </div>
        )}
        {activeTab === 'levelization' && (
          <div className="flex-1 min-h-0">
            <LevelizationView sources={view.levelized_sources ?? []} />
          </div>
        )}
      </div>

      {aiEnabled && showInsights && (
        <InsightsPanel
          session={session}
          result={buildAiContext(
            view,
            {
              label: rangeLabel ?? 'All reporting months',
              months_included: activeMonths.map(m => m.label),
              months_excluded: rangeMonths.filter(m => excluded.includes(m.key)).map(m => m.label),
              months_available: months.length,
              reporting_years: result.reporting_years ?? [],
            },
            bopTargets,
          )}
          contextLabel={rangeLabel ?? 'All reporting months'}
          onClose={() => setShowInsights(false)}
        />
      )}
    </div>
  );
};

GconResults.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    bop_targets: PropTypes.object,
    result: PropTypes.object.isRequired,
  }).isRequired,
};

export default GconResults;
